import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import UserSearchSelect from '../components/UserSearchSelect';
import { getStoredUser, parseJsonSafe } from '../api/client';
import { createParentUser, createStudentUser, createTutorUser } from '../api/admin';
import { getMyTutorStudents } from '../api/me';
import { formatBoolean, formatRole } from '../lib/format';
import {
  ACCOUNT_CREATED_AND_INVITE_SENT_SUMMARY,
  ACTIVE_STUDENT_INVITE_SUMMARY,
  ADMIN_INVITE_SENT_NEXT_STEP,
  ADMIN_TUTOR_LINK_NEXT_STEP,
  GUARDIAN_UNLOCK_REQUIREMENT,
  REUSED_PARENT_NEXT_STEP,
  REUSED_PARENT_SUMMARY,
  TUTOR_AUTO_LINK_NEXT_STEP,
  TUTOR_INVITE_SENT_NEXT_STEP,
  TUTOR_UNLINKED_PARENT_INVITE_SENT_NEXT_STEP,
} from '../copy/onboardingStatus';

const RELATIONSHIP_OPTIONS = ['parent', 'guardian', 'grandparent', 'other'];

function extractErrorMessage(data, fallback) {
  if (data?.detail?.code === 'email_exists_with_different_role') {
    return 'This email is already used by a different account type. Use a different email or sign in to the existing account.';
  }
  const detail = data?.detail;
  return (
    (typeof detail === 'string' && detail) ||
    (typeof detail?.message === 'string' && detail.message) ||
    (typeof data?.message === 'string' && data.message) ||
    fallback
  );
}

function deriveSchoolYear(dateOfBirthIso) {
  if (!dateOfBirthIso) {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateOfBirthIso).trim());
  if (!match) {
    return null;
  }
  const dobYear = Number(match[1]);
  const dobMonth = Number(match[2]);
  const dobDay = Number(match[3]);
  if (!Number.isInteger(dobYear) || !Number.isInteger(dobMonth) || !Number.isInteger(dobDay)) {
    return null;
  }
  const dob = new Date(Date.UTC(dobYear, dobMonth - 1, dobDay));
  if (
    Number.isNaN(dob.getTime())
    || dob.getUTCFullYear() !== dobYear
    || dob.getUTCMonth() + 1 !== dobMonth
    || dob.getUTCDate() !== dobDay
  ) {
    return null;
  }

  const now = new Date();
  const todayYear = now.getUTCFullYear();
  const todayMonth = now.getUTCMonth() + 1;
  const academicYearEndYear = todayMonth >= 9 ? todayYear + 1 : todayYear;
  const targetMonth = 8;
  const targetDay = 31;

  let ageOnAcademicYearEnd = academicYearEndYear - dobYear;
  if (dobMonth > targetMonth || (dobMonth === targetMonth && dobDay > targetDay)) {
    ageOnAcademicYearEnd -= 1;
  }

  const schoolYear = ageOnAcademicYearEnd - 5;

  if (schoolYear < 1 || schoolYear > 13) {
    return null;
  }

  return schoolYear;
}

function computeUnder18(dateOfBirthIso) {
  if (!dateOfBirthIso) {
    return null;
  }
  const dob = new Date(`${dateOfBirthIso}T00:00:00`);
  if (Number.isNaN(dob.getTime())) {
    return null;
  }
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age < 18;
}

function formatSchoolYearPreview(schoolYear) {
  if (schoolYear === null) {
    return 'Not available yet';
  }
  return String(schoolYear);
}

function FieldError({ message }) {
  if (!message) {
    return null;
  }
  return <p className="field-error" role="alert">{message}</p>;
}

function ReviewRow({ label, value }) {
  const displayValue = value || '-';
  return (
    <li className="wizard-review-item">
      <p className="wizard-review-line">
        <strong>{`${label}: ${displayValue}`}</strong>
      </p>
    </li>
  );
}

function UserCreationWizardPage() {
  const navigate = useNavigate();
  const currentUser = getStoredUser();
  const role = currentUser?.role || null;
  const isAdmin = role === 'ADMIN';
  const isTutor = role === 'TUTOR';

  const roleOptions = useMemo(() => {
    if (isAdmin) {
      return [
        { value: 'TUTOR', label: 'Create Tutor' },
        { value: 'STUDENT', label: 'Create Student' },
        { value: 'PARENT', label: 'Create Parent' },
      ];
    }
    if (isTutor) {
      return [
        { value: 'STUDENT', label: 'Create Student' },
        { value: 'PARENT', label: 'Create Parent' },
      ];
    }
    return [];
  }, [isAdmin, isTutor]);

  const [wizardType, setWizardType] = useState(roleOptions[0]?.value || 'STUDENT');
  const [step, setStep] = useState(1);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const [tutorForm, setTutorForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    organisation_name: '',
    timezone: '',
  });

  const [studentForm, setStudentForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    preferred_name: '',
    timezone: '',
    inviteParent: false,
    parent_email: '',
    parent_first_name: '',
    parent_last_name: '',
    relationship_to_student: '',
    phone_number: '',
    parent_timezone: '',
  });

  const [parentForm, setParentForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    relationship_to_student: '',
    phone_number: '',
    timezone: '',
    studentUsers: [],
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [tutorStudents, setTutorStudents] = useState([]);

  useEffect(() => {
    setWizardType(roleOptions[0]?.value || 'STUDENT');
  }, [roleOptions]);

  useEffect(() => {
    if (!(isTutor && wizardType === 'PARENT')) {
      return;
    }
    let mounted = true;
    const loadTutorStudents = async () => {
      try {
        const response = await getMyTutorStudents();
        const data = await parseJsonSafe(response);
        if (!response.ok) {
          return;
        }
        if (mounted) {
          setTutorStudents(Array.isArray(data) ? data : []);
        }
      } catch (_error) {
        if (mounted) {
          setTutorStudents([]);
        }
      }
    };
    loadTutorStudents();
    return () => {
      mounted = false;
    };
  }, [isTutor, wizardType]);

  const studentSchoolYear = deriveSchoolYear(studentForm.date_of_birth);
  const studentUnder18 = computeUnder18(studentForm.date_of_birth);

  const resetFlow = (nextType = wizardType) => {
    setWizardType(nextType);
    setStep(1);
    setSubmitError('');
    setFieldErrors({});
    setSuccessData(null);
  };

  const cancelFlow = () => {
    navigate(isAdmin ? '/admin/users' : '/tutor/students');
  };

  const validateEmail = (value) => /.+@.+\..+/.test(String(value || '').trim());

  const validateStep = () => {
    const errors = {};

    if (wizardType === 'TUTOR' && step === 1) {
      if (!validateEmail(tutorForm.email)) {
        errors.email = 'Valid email is required.';
      }
      if (!tutorForm.first_name.trim()) {
        errors.first_name = 'First name is required.';
      }
      if (!tutorForm.last_name.trim()) {
        errors.last_name = 'Last name is required.';
      }
    }

    if (wizardType === 'STUDENT') {
      if (step === 1) {
        if (!validateEmail(studentForm.email)) {
          errors.email = 'Valid email is required.';
        }
        if (!studentForm.first_name.trim()) {
          errors.first_name = 'First name is required.';
        }
        if (!studentForm.last_name.trim()) {
          errors.last_name = 'Last name is required.';
        }
        if (!studentForm.date_of_birth) {
          errors.date_of_birth = 'Date of birth is required.';
        } else {
          const dob = new Date(`${studentForm.date_of_birth}T00:00:00`);
          if (Number.isNaN(dob.getTime()) || dob > new Date()) {
            errors.date_of_birth = 'Date of birth must be valid and not in the future.';
          }
        }
      }
      if (step === 2 && studentForm.inviteParent) {
        if (!validateEmail(studentForm.parent_email)) {
          errors.parent_email = 'Valid parent email is required.';
        }
        if (!studentForm.parent_first_name.trim()) {
          errors.parent_first_name = 'Parent first name is required.';
        }
        if (!studentForm.parent_last_name.trim()) {
          errors.parent_last_name = 'Parent last name is required.';
        }
        if (!RELATIONSHIP_OPTIONS.includes(studentForm.relationship_to_student)) {
          errors.relationship_to_student = 'Relationship is required.';
        }
      }
    }

    if (wizardType === 'PARENT' && step === 1) {
      if (!validateEmail(parentForm.email)) {
        errors.email = 'Valid email is required.';
      }
      if (!parentForm.first_name.trim()) {
        errors.first_name = 'First name is required.';
      }
      if (!parentForm.last_name.trim()) {
        errors.last_name = 'Last name is required.';
      }
      if (!RELATIONSHIP_OPTIONS.includes(parentForm.relationship_to_student)) {
        errors.relationship_to_student = 'Relationship is required.';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const totalSteps = wizardType === 'TUTOR' ? 3 : wizardType === 'STUDENT' ? 3 : 4;

  const goNext = () => {
    if (!validateStep()) {
      return;
    }
    setSubmitError('');
    setStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const goBack = () => {
    setSubmitError('');
    setFieldErrors({});
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const submitWizard = async () => {
    if (!validateStep()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      if (wizardType === 'TUTOR') {
        const response = await createTutorUser({
          email: tutorForm.email.trim(),
          first_name: tutorForm.first_name.trim(),
          last_name: tutorForm.last_name.trim(),
          organisation_name: tutorForm.organisation_name.trim() || null,
          timezone: tutorForm.timezone.trim() || null,
        });
        const data = await parseJsonSafe(response);
        if (!response.ok) {
          throw new Error(extractErrorMessage(data, 'Failed to create tutor.'));
        }
        setSuccessData({ type: 'TUTOR', data });
        return;
      }

      if (wizardType === 'STUDENT') {
        const studentPayload = {
          email: studentForm.email.trim(),
          first_name: studentForm.first_name.trim(),
          last_name: studentForm.last_name.trim(),
          date_of_birth: studentForm.date_of_birth,
          preferred_name: studentForm.preferred_name.trim() || null,
          timezone: studentForm.timezone.trim() || null,
        };

        const studentRes = await createStudentUser(studentPayload);
        const studentData = await parseJsonSafe(studentRes);
        if (!studentRes.ok) {
          throw new Error(extractErrorMessage(studentData, 'Failed to create student.'));
        }

        let parentData = null;
        if (studentForm.inviteParent) {
          const parentRes = await createParentUser({
            email: studentForm.parent_email.trim(),
            first_name: studentForm.parent_first_name.trim(),
            last_name: studentForm.parent_last_name.trim(),
            relationship_to_student: studentForm.relationship_to_student,
            phone_number: studentForm.phone_number.trim() || null,
            timezone: studentForm.parent_timezone.trim() || null,
            student_ids: [Number(studentData.student_id)],
          });
          parentData = await parseJsonSafe(parentRes);
          if (!parentRes.ok) {
            throw new Error(extractErrorMessage(parentData, 'Student created, but parent invite failed.'));
          }
        }

        setSuccessData({ type: 'STUDENT', data: { student: studentData, parent: parentData } });
        return;
      }

      const parentPayload = {
        email: parentForm.email.trim(),
        first_name: parentForm.first_name.trim(),
        last_name: parentForm.last_name.trim(),
        relationship_to_student: parentForm.relationship_to_student,
        phone_number: parentForm.phone_number.trim() || null,
        timezone: parentForm.timezone.trim() || null,
        student_ids: (parentForm.studentUsers || []).map((item) => Number(item.id)),
      };

      const parentRes = await createParentUser(parentPayload);
      const parentData = await parseJsonSafe(parentRes);
      if (!parentRes.ok) {
        throw new Error(extractErrorMessage(parentData, 'Failed to create parent.'));
      }
      setSuccessData({ type: 'PARENT', data: parentData });
    } catch (error) {
      setSubmitError(error.message || 'Submission failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitStepForType = wizardType === 'TUTOR' ? 3 : wizardType === 'STUDENT' ? 3 : 4;

  const wizardConfig = {
    TUTOR: {
      eyebrow: 'Tutor setup',
      title: 'Provision a tutor invite',
      description: 'Create a tutor account and issue an invite without bypassing the documented onboarding workflow.',
      steps: ['Tutor details', 'Review', 'Submit'],
    },
    STUDENT: {
      eyebrow: 'Student setup',
      title: 'Create student access',
      description: 'Capture student profile details first, then optionally add the linked parent invite before creation.',
      steps: ['Student details', 'Parent invite', 'Review'],
    },
    PARENT: {
      eyebrow: 'Parent setup',
      title: 'Invite and link a parent',
      description: 'Create or reuse the parent account, then attach one or more students through the approved linking flow.',
      steps: ['Parent details', 'Linked students', 'Review', 'Submit'],
    },
  };

  const currentWizardConfig = wizardConfig[wizardType] || wizardConfig.STUDENT;
  const progressPercent = `${Math.round((Math.min(step, submitStepForType) / submitStepForType) * 100)}%`;

  const renderWizardChooser = () => (
    <Card className="wizard-chooser-card">
      <div className="section-header">
        <div>
          <p className="eyebrow">Creation flow</p>
          <h2>Choose creation flow</h2>
        </div>
        <p className="muted-text">Available flows depend on your current role and stay within the onboarding contract.</p>
      </div>
      <div className="wizard-type-grid">
        {roleOptions.map((option) => (
          <label
            key={option.value}
            className={`wizard-type-option ${wizardType === option.value ? 'is-active' : ''}`}
          >
            <input
              type="radio"
              name="wizard-type"
              value={option.value}
              checked={wizardType === option.value}
              onChange={() => resetFlow(option.value)}
            />
            <span className="wizard-type-label">{option.label}</span>
            <span className="wizard-type-helper">
              {option.value === 'TUTOR'
                ? 'Invite a tutor account'
                : option.value === 'STUDENT'
                  ? 'Create a learner and optional guardian invite'
                  : 'Link a parent or guardian to existing students'}
            </span>
          </label>
        ))}
      </div>
    </Card>
  );

  const renderTutorStep = () => {
    if (step === 1) {
      return (
        <Card>
          <h2>Tutor details</h2>
          <div className="stack-md">
            <div>
              <label htmlFor="tutor-email">Email</label>
              <input
                id="tutor-email"
                type="email"
                value={tutorForm.email}
                onChange={(event) => setTutorForm((prev) => ({ ...prev, email: event.target.value }))}
              />
              <FieldError message={fieldErrors.email} />
            </div>
            <div>
              <label htmlFor="tutor-first-name">First name</label>
              <input
                id="tutor-first-name"
                type="text"
                value={tutorForm.first_name}
                onChange={(event) => setTutorForm((prev) => ({ ...prev, first_name: event.target.value }))}
              />
              <FieldError message={fieldErrors.first_name} />
            </div>
            <div>
              <label htmlFor="tutor-last-name">Last name</label>
              <input
                id="tutor-last-name"
                type="text"
                value={tutorForm.last_name}
                onChange={(event) => setTutorForm((prev) => ({ ...prev, last_name: event.target.value }))}
              />
              <FieldError message={fieldErrors.last_name} />
            </div>
            <div>
              <label htmlFor="tutor-organisation">Organisation name (optional)</label>
              <input
                id="tutor-organisation"
                type="text"
                value={tutorForm.organisation_name}
                onChange={(event) => setTutorForm((prev) => ({ ...prev, organisation_name: event.target.value }))}
              />
            </div>
            <div>
              <label htmlFor="tutor-timezone">Timezone (optional)</label>
              <input
                id="tutor-timezone"
                type="text"
                value={tutorForm.timezone}
                onChange={(event) => setTutorForm((prev) => ({ ...prev, timezone: event.target.value }))}
                placeholder="e.g. Europe/London"
              />
            </div>
          </div>
        </Card>
      );
    }

    return (
        <Card>
          <h2>Review</h2>
          <p className="muted-text">Check details before creating the tutor invite.</p>
        <ul className="wizard-review-list">
          <ReviewRow label="Email" value={tutorForm.email} />
          <ReviewRow label="Name" value={`${tutorForm.first_name} ${tutorForm.last_name}`.trim()} />
          <ReviewRow label="Organisation" value={tutorForm.organisation_name} />
          <ReviewRow label="Timezone" value={tutorForm.timezone} />
        </ul>
      </Card>
    );
  };

  const renderStudentStep = () => {
    if (step === 1) {
      return (
        <Card>
          <h2>Student details</h2>
          <div className="stack-md">
            <div>
              <label htmlFor="student-email">Email</label>
              <input
                id="student-email"
                type="email"
                value={studentForm.email}
                onChange={(event) => setStudentForm((prev) => ({ ...prev, email: event.target.value }))}
              />
              <FieldError message={fieldErrors.email} />
            </div>
            <div>
              <label htmlFor="student-first-name">First name</label>
              <input
                id="student-first-name"
                type="text"
                value={studentForm.first_name}
                onChange={(event) => setStudentForm((prev) => ({ ...prev, first_name: event.target.value }))}
              />
              <FieldError message={fieldErrors.first_name} />
            </div>
            <div>
              <label htmlFor="student-last-name">Last name</label>
              <input
                id="student-last-name"
                type="text"
                value={studentForm.last_name}
                onChange={(event) => setStudentForm((prev) => ({ ...prev, last_name: event.target.value }))}
              />
              <FieldError message={fieldErrors.last_name} />
            </div>
            <div>
              <label htmlFor="student-dob">Date of birth</label>
              <input
                id="student-dob"
                type="date"
                value={studentForm.date_of_birth}
                onChange={(event) => setStudentForm((prev) => ({ ...prev, date_of_birth: event.target.value }))}
              />
              <FieldError message={fieldErrors.date_of_birth} />
            </div>
            <div>
              <label htmlFor="student-preferred-name">Preferred name (optional)</label>
              <input
                id="student-preferred-name"
                type="text"
                value={studentForm.preferred_name}
                onChange={(event) => setStudentForm((prev) => ({ ...prev, preferred_name: event.target.value }))}
              />
            </div>
            <div>
              <label htmlFor="student-timezone">Timezone (optional)</label>
              <input
                id="student-timezone"
                type="text"
                value={studentForm.timezone}
                onChange={(event) => setStudentForm((prev) => ({ ...prev, timezone: event.target.value }))}
              />
            </div>
            <div className="notice notice-info">
              <p>Derived school year: <strong>{formatSchoolYearPreview(studentSchoolYear)}</strong></p>
              <p>Under 18: <strong>{formatBoolean(studentUnder18)}</strong></p>
            </div>
            {studentUnder18 ? (
              <p className="notice notice-warn">Under-18 students require a verified parent before system use.</p>
            ) : null}
          </div>
        </Card>
      );
    }

    if (step === 2) {
      return (
        <Card>
          <h2>Optional parent invite</h2>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={studentForm.inviteParent}
              onChange={(event) => setStudentForm((prev) => ({ ...prev, inviteParent: event.target.checked }))}
            />
            <span>Invite a parent now</span>
          </label>

          {studentForm.inviteParent ? (
            <div className="stack-md inline-control">
              <div>
                <label htmlFor="parent-email">Parent email</label>
                <input
                  id="parent-email"
                  type="email"
                  value={studentForm.parent_email}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, parent_email: event.target.value }))}
                />
                <FieldError message={fieldErrors.parent_email} />
              </div>
              <div>
                <label htmlFor="parent-first-name">First name</label>
                <input
                  id="parent-first-name"
                  type="text"
                  value={studentForm.parent_first_name}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, parent_first_name: event.target.value }))}
                />
                <FieldError message={fieldErrors.parent_first_name} />
              </div>
              <div>
                <label htmlFor="parent-last-name">Last name</label>
                <input
                  id="parent-last-name"
                  type="text"
                  value={studentForm.parent_last_name}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, parent_last_name: event.target.value }))}
                />
                <FieldError message={fieldErrors.parent_last_name} />
              </div>
              <div>
                <label htmlFor="parent-relationship">Relationship to student</label>
                <select
                  id="parent-relationship"
                  value={studentForm.relationship_to_student}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, relationship_to_student: event.target.value }))}
                >
                  <option value="">Select relationship</option>
                  {RELATIONSHIP_OPTIONS.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
                <FieldError message={fieldErrors.relationship_to_student} />
              </div>
              <div>
                <label htmlFor="parent-phone">Phone number (optional)</label>
                <input
                  id="parent-phone"
                  type="text"
                  value={studentForm.phone_number}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, phone_number: event.target.value }))}
                />
              </div>
              <div>
                <label htmlFor="parent-timezone">Timezone (optional)</label>
                <input
                  id="parent-timezone"
                  type="text"
                  value={studentForm.parent_timezone}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, parent_timezone: event.target.value }))}
                />
              </div>
            </div>
          ) : null}
        </Card>
      );
    }

    return (
      <Card>
        <h2>Review</h2>
        <ul className="wizard-review-list">
          <ReviewRow label="Email" value={studentForm.email} />
          <ReviewRow label="Name" value={`${studentForm.first_name} ${studentForm.last_name}`.trim()} />
          <ReviewRow label="Date of birth" value={studentForm.date_of_birth} />
          <ReviewRow label="Preferred name" value={studentForm.preferred_name} />
          <ReviewRow label="Timezone" value={studentForm.timezone} />
          <ReviewRow label="School year (preview)" value={formatSchoolYearPreview(studentSchoolYear)} />
          <ReviewRow label="Under 18" value={formatBoolean(studentUnder18)} />
          <ReviewRow
            label="Tutor assignment"
            value={isAdmin ? 'Applied after creation via separate tutor-link flow' : `Current tutor auto-linked (${currentUser?.email || currentUser?.id})`}
          />
          <ReviewRow label="Parent invite" value={studentForm.inviteParent ? 'Yes' : 'No'} />
        </ul>
      </Card>
    );
  };

  const toggleTutorStudent = (student) => {
    setParentForm((prev) => {
      const exists = (prev.studentUsers || []).some((item) => Number(item.id) === Number(student.id));
      if (exists) {
        return {
          ...prev,
          studentUsers: (prev.studentUsers || []).filter((item) => Number(item.id) !== Number(student.id)),
        };
      }
      return {
        ...prev,
        studentUsers: [...(prev.studentUsers || []), student],
      };
    });
  };

  const renderParentStep = () => {
    if (step === 1) {
      return (
        <Card>
          <h2>Parent details</h2>
          <div className="stack-md">
            <div>
              <label htmlFor="wizard-parent-email">Email</label>
              <input
                id="wizard-parent-email"
                type="email"
                value={parentForm.email}
                onChange={(event) => setParentForm((prev) => ({ ...prev, email: event.target.value }))}
              />
              <FieldError message={fieldErrors.email} />
            </div>
            <div>
              <label htmlFor="wizard-parent-first-name">First name</label>
              <input
                id="wizard-parent-first-name"
                type="text"
                value={parentForm.first_name}
                onChange={(event) => setParentForm((prev) => ({ ...prev, first_name: event.target.value }))}
              />
              <FieldError message={fieldErrors.first_name} />
            </div>
            <div>
              <label htmlFor="wizard-parent-last-name">Last name</label>
              <input
                id="wizard-parent-last-name"
                type="text"
                value={parentForm.last_name}
                onChange={(event) => setParentForm((prev) => ({ ...prev, last_name: event.target.value }))}
              />
              <FieldError message={fieldErrors.last_name} />
            </div>
            <div>
              <label htmlFor="wizard-parent-relationship">Relationship to student</label>
              <select
                id="wizard-parent-relationship"
                value={parentForm.relationship_to_student}
                onChange={(event) => setParentForm((prev) => ({ ...prev, relationship_to_student: event.target.value }))}
              >
                <option value="">Select relationship</option>
                {RELATIONSHIP_OPTIONS.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
              <FieldError message={fieldErrors.relationship_to_student} />
            </div>
            <div>
              <label htmlFor="wizard-parent-phone">Phone number (optional)</label>
              <input
                id="wizard-parent-phone"
                type="text"
                value={parentForm.phone_number}
                onChange={(event) => setParentForm((prev) => ({ ...prev, phone_number: event.target.value }))}
              />
            </div>
            <div>
              <label htmlFor="wizard-parent-timezone">Timezone (optional)</label>
              <input
                id="wizard-parent-timezone"
                type="text"
                value={parentForm.timezone}
                onChange={(event) => setParentForm((prev) => ({ ...prev, timezone: event.target.value }))}
              />
            </div>
          </div>
        </Card>
      );
    }

    if (step === 2) {
      return (
        <Card>
          <h2>Link students (optional)</h2>
          {isAdmin ? (
            <UserSearchSelect
              label="Students"
              placeholder="Search student email"
              role="STUDENT"
              multiple
              selected={parentForm.studentUsers}
              onChange={(value) => setParentForm((prev) => ({ ...prev, studentUsers: value || [] }))}
              helpText="Select one or more students, or skip this step."
            />
          ) : (
            <div className="stack-sm">
              {tutorStudents.length === 0 ? (
                <p className="notice">No linked students available yet.</p>
              ) : tutorStudents.map((student) => {
                const checked = (parentForm.studentUsers || []).some((item) => Number(item.id) === Number(student.id));
                return (
                  <label key={student.id} className="checkbox-row notice">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleTutorStudent(student)}
                    />
                    <span>{student.display_name || student.student_display_name || `Student ${student.id}`} (id: {student.id})</span>
                  </label>
                );
              })}
            </div>
          )}
        </Card>
      );
    }

    return (
      <Card>
        <h2>Review</h2>
        <ul className="wizard-review-list">
          <ReviewRow label="Email" value={parentForm.email} />
          <ReviewRow label="Name" value={`${parentForm.first_name} ${parentForm.last_name}`.trim()} />
          <ReviewRow label="Relationship" value={parentForm.relationship_to_student} />
          <ReviewRow label="Phone number" value={parentForm.phone_number} />
          <ReviewRow label="Timezone" value={parentForm.timezone} />
          <ReviewRow
            label="Linked students"
            value={(parentForm.studentUsers || []).length > 0
              ? parentForm.studentUsers.map((student) => student.email || student.display_name || student.id).join(', ')
              : 'None selected'}
          />
        </ul>
      </Card>
    );
  };

  const renderStepContent = () => {
    if (successData) {
      const isStudent = successData.type === 'STUDENT';
      const studentResult = isStudent
        ? (successData.data?.student || successData.data || null)
        : null;
      const parentResult = isStudent
        ? (successData.data?.parent || null)
        : (successData.data?.parent || successData.data || null);
      const guardianPending = Boolean(studentResult?.guardian_pending);
      const parentReused = Boolean(parentResult?.reused_existing_parent);
      const inviteSent = isStudent
        ? Boolean(studentResult?.invite_sent)
        : Boolean(parentResult?.invite_sent);
      const tutorCanResendInvite = isStudent
        || (parentResult?.linked_student_ids || []).length > 0;
      const parentInviteRequested = isStudent && studentForm.inviteParent;
      const roleLabel = formatRole(successData.type);
      return (
        <Card className="wizard-success-card">
          <div className="section-header">
            <div>
              <p className="eyebrow">Success</p>
              <h2>{roleLabel} created</h2>
            </div>
            <span className="status-pill status-pill-marked">Completed</span>
          </div>
          {isStudent && !guardianPending && inviteSent ? (
            <p className="notice notice-info">
              {ACTIVE_STUDENT_INVITE_SUMMARY}
            </p>
          ) : null}
          {!isStudent && !parentReused && inviteSent ? (
            <p className="notice notice-info">
              {ACCOUNT_CREATED_AND_INVITE_SENT_SUMMARY}
            </p>
          ) : null}
          {parentReused ? (
            <p className="notice notice-info">
              {REUSED_PARENT_SUMMARY}
            </p>
          ) : null}
          {!parentReused && !inviteSent ? (
            <p className="notice notice-warn" role="alert">
              The account was created, but the invite email failed to send. Resend the invite before asking the user to complete setup.
            </p>
          ) : null}
          {guardianPending ? (
            <p className="notice notice-warn">
              Student is currently guardian pending. {GUARDIAN_UNLOCK_REQUIREMENT}
            </p>
          ) : null}
          {guardianPending && isStudent && parentReused ? (
            <p className="muted-text">
              A parent account was already linked for this student. Ask that parent to sign in and complete any outstanding invite or account-setup steps.
            </p>
          ) : null}
          {guardianPending && parentInviteRequested && !parentReused && Boolean(parentResult?.invite_sent) ? (
            <p className="muted-text">
              A parent invite was sent. Student access will unlock after that parent accepts the invite and becomes verified.
            </p>
          ) : null}
          {isStudent ? (
            <p className="muted-text">
              {isAdmin
                ? ADMIN_TUTOR_LINK_NEXT_STEP
                : TUTOR_AUTO_LINK_NEXT_STEP}
            </p>
          ) : null}
          {parentReused ? (
            <p className="muted-text">
              {REUSED_PARENT_NEXT_STEP}
            </p>
          ) : inviteSent ? (
            <p className="muted-text">
              {isTutor
                ? (tutorCanResendInvite
                  ? TUTOR_INVITE_SENT_NEXT_STEP
                  : TUTOR_UNLINKED_PARENT_INVITE_SENT_NEXT_STEP)
                : ADMIN_INVITE_SENT_NEXT_STEP}
            </p>
          ) : (
            <p className="muted-text">
              The invite must be resent before this user can finish account setup.
            </p>
          )}
          <details>
            <summary>Technical details</summary>
            <pre className="wizard-success-json">{JSON.stringify(successData.data, null, 2)}</pre>
          </details>
          <div className="meta-row">
            {isAdmin ? <Link to="/admin/users" className="btn btn-tertiary">Open Admin: Users</Link> : null}
            {isTutor ? <Link to="/tutor/students" className="btn btn-tertiary">Go to My students</Link> : null}
          </div>
          <div className="meta-row">
            <button type="button" className="btn btn-secondary" onClick={() => resetFlow(wizardType)}>Create another</button>
            <button type="button" className="btn btn-primary" onClick={cancelFlow}>Done</button>
          </div>
        </Card>
      );
    }

    if (wizardType === 'TUTOR') {
      return renderTutorStep();
    }
    if (wizardType === 'STUDENT') {
      return renderStudentStep();
    }
    return renderParentStep();
  };

  return (
    <div className="page page-shell page-stack wizard-page">
      <section className="page-header">
        <div>
          <p className="eyebrow">Onboarding</p>
          <h1>User creation wizard</h1>
          {currentUser?.email ? <p className="muted-text">Signed in as {currentUser.email}</p> : null}
          <p>Create tutor, student, and parent accounts through the documented invitation and linking workflow.</p>
        </div>
      </section>
      {renderWizardChooser()}
      <section className="wizard-layout">
        <Card className="wizard-sidebar-card">
          <div className="stack-md">
            <div>
              <p className="eyebrow">{currentWizardConfig.eyebrow}</p>
              <h2>{currentWizardConfig.title}</h2>
              <p>{currentWizardConfig.description}</p>
            </div>
            <div className="wizard-progress-shell" aria-label={`Step ${Math.min(step, submitStepForType)} of ${submitStepForType}`}>
              <div className="wizard-progress-track">
                <span className="wizard-progress-fill" style={{ width: progressPercent }} />
              </div>
              <p className="muted-text">Step {Math.min(step, submitStepForType)} of {submitStepForType}</p>
            </div>
            <ol className="wizard-step-list">
              {currentWizardConfig.steps.map((stepLabel, index) => {
                const stepNumber = index + 1;
                const stateClass = stepNumber < step ? 'is-complete' : stepNumber === step ? 'is-current' : '';
                return (
                  <li key={stepLabel} className={`wizard-step-item ${stateClass}`.trim()}>
                    <span className="wizard-step-index">{stepNumber}</span>
                    <div>
                      <strong>{stepLabel}</strong>
                      <p className="muted-text">
                        {stepNumber < step ? 'Completed' : stepNumber === step ? 'Current step' : 'Upcoming'}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </Card>
        <div className="wizard-main-column">
          {renderStepContent()}
        </div>
      </section>
      {submitError ? <p role="alert" className="notice notice-error">{submitError}</p> : null}

      {!successData ? (
        <div className="meta-row">
          <button type="button" className="btn btn-tertiary" onClick={cancelFlow}>Cancel</button>
          {step > 1 ? (
            <button type="button" className="btn btn-secondary" onClick={goBack}>Back</button>
          ) : null}
          {step < submitStepForType ? (
            <button type="button" className="btn btn-primary" onClick={goNext}>Next</button>
          ) : (
            <button type="button" className="btn btn-primary" onClick={submitWizard} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default UserCreationWizardPage;
